import { environment } from "../environments/environment";
import {
  WebSocketEvent,
  WebSocketChatMessage,
  WebSocketStatusEvent,
  WebSocketResponseEvent,
  WebSocketErrorEvent,
  WebSocketStreamChunkEvent,
  WebSocketStreamDoneEvent,
  WebSocketStreamErrorEvent,
} from "../Interfaces/interface.chat";

type WebSocketStatus = "connecting" | "connected" | "disconnected" | "error";

export interface ChatWebSocketCallbacks {
  onStatus?: (event: WebSocketStatusEvent) => void;
  onResponse?: (event: WebSocketResponseEvent) => void;
  onError?: (event: WebSocketErrorEvent) => void;
  onStreamChunk?: (event: WebSocketStreamChunkEvent) => void;
  onStreamDone?: (event: WebSocketStreamDoneEvent) => void;
  onStreamError?: (event: WebSocketStreamErrorEvent) => void;
  onConnect?: () => void;
  onDisconnect?: () => void;
  onReconnect?: () => void;
}

class ChatWebSocketService {
  private ws: WebSocket | null = null;
  private clientId: string | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 1000; // Start with 1 second
  private reconnectTimer: NodeJS.Timeout | null = null;
  private callbacks: ChatWebSocketCallbacks = {};
  private status: WebSocketStatus = "disconnected";
  private activeJobId: string | null = null;

  /**
   * Convert HTTP/HTTPS URL to WebSocket URL
   */
  private getWebSocketUrl(clientId: string): string {
    const baseUrl = environment.ELVA_HOST_URL;
    const wsProtocol = baseUrl.startsWith("https") ? "wss" : "ws";
    const wsHost = baseUrl.replace(/^https?:\/\//, "");
    return `${wsProtocol}://${wsHost}/api/${environment.ELVA_API_VERSION}/ws/chat/${clientId}`;
  }

  /**
   * Generate a unique client ID
   */
  private generateClientId(): string {
    return `client_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Connect to WebSocket server
   */
  public connect(clientId?: string, callbacks?: ChatWebSocketCallbacks): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      console.warn("WebSocket already connected");
      return;
    }

    if (callbacks) {
      this.callbacks = callbacks;
    }

    this.clientId = clientId || this.generateClientId();
    const wsUrl = this.getWebSocketUrl(this.clientId);

    try {
      this.status = "connecting";
      this.ws = new WebSocket(wsUrl);

      this.ws.onopen = () => {
        console.info("Chat WebSocket connected:", this.clientId);
        this.status = "connected";
        this.reconnectAttempts = 0;
        this.reconnectDelay = 1000;
        this.callbacks.onConnect?.();
      };

      this.ws.onmessage = (event) => {
        try {
          const data: WebSocketEvent = JSON.parse(event.data);
          this.handleMessage(data);
        } catch (error) {
          console.error("Failed to parse WebSocket message:", error);
          this.callbacks.onError?.({
            type: "error",
            data: "Failed to parse server message",
            timestamp: new Date().toISOString(),
          });
        }
      };

      this.ws.onerror = (error) => {
        console.error("Chat WebSocket error:", error);
        this.status = "error";
        this.callbacks.onError?.({
          type: "error",
          data: "WebSocket connection error",
          timestamp: new Date().toISOString(),
        });
      };

      this.ws.onclose = (event) => {
        console.warn("Chat WebSocket closed:", event.code, event.reason);
        this.status = "disconnected";
        this.callbacks.onDisconnect?.();

        // Attempt reconnection if not a normal closure
        if (
          event.code !== 1000 &&
          this.reconnectAttempts < this.maxReconnectAttempts
        ) {
          this.scheduleReconnect();
        }
      };
    } catch (error) {
      console.error("Failed to create WebSocket connection:", error);
      this.status = "error";
      this.callbacks.onError?.({
        type: "error",
        data: "Failed to create WebSocket connection",
        timestamp: new Date().toISOString(),
      });
    }
  }

  /**
   * Handle incoming WebSocket messages
   */
  private handleMessage(event: WebSocketEvent): void {
    switch (event.type) {
      case "status":
        if (event.job_id) {
          this.activeJobId = event.job_id;
        }
        this.callbacks.onStatus?.(event as WebSocketStatusEvent);
        break;
      case "response":
        this.activeJobId = null; // Job completed
        this.callbacks.onResponse?.(event as WebSocketResponseEvent);
        break;
      case "error":
        this.callbacks.onError?.(event as WebSocketErrorEvent);
        break;
      case "stream_chunk":
        this.callbacks.onStreamChunk?.(event as WebSocketStreamChunkEvent);
        break;
      case "stream_done":
        this.activeJobId = null;
        this.callbacks.onStreamDone?.(event as WebSocketStreamDoneEvent);
        break;
      case "stream_error":
        this.callbacks.onStreamError?.(event as WebSocketStreamErrorEvent);
        break;
      default:
        console.warn("Unknown WebSocket event type:", event);
    }
  }

  /**
   * Schedule reconnection attempt
   */
  private scheduleReconnect(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
    }

    this.reconnectAttempts++;
    const delay = Math.min(
      this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1),
      30000, // Max 30 seconds
    );

    console.info(
      `Scheduling WebSocket reconnection attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts} in ${delay}ms`,
    );

    this.reconnectTimer = setTimeout(() => {
      if (this.status !== "connected" && this.clientId) {
        console.info("Attempting WebSocket reconnection...");
        this.callbacks.onReconnect?.();
        this.connect(this.clientId, this.callbacks);
      }
    }, delay);
  }

  /**
   * Send a chat message via WebSocket
   */
  public sendMessage(message: WebSocketChatMessage): boolean {
    console.log("message: ", message);
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      console.error("WebSocket is not connected");
      return false;
    }

    try {
      this.ws.send(JSON.stringify(message));
      return true;
    } catch (error) {
      console.error("Failed to send WebSocket message:", error);
      return false;
    }
  }

  /**
   * Get current connection status
   */
  public getStatus(): WebSocketStatus {
    return this.status;
  }

  /**
   * Get current client ID
   */
  public getClientId(): string | null {
    return this.clientId;
  }

  /**
   * Get active job ID
   */
  public getActiveJobId(): string | null {
    return this.activeJobId;
  }

  /**
   * Check if WebSocket is connected
   */
  public isConnected(): boolean {
    console.log("this.ws?.readyState: ", this.ws?.readyState);
    console.log("WebSocket.OPEN: ", WebSocket.OPEN);
    return this.ws?.readyState === WebSocket.OPEN;
  }

  /**
   * Get current WebSocket readyState
   */
  public getReadyState(): number | undefined {
    return this.ws?.readyState;
  }

  /**
   * Disconnect from WebSocket server
   */
  public disconnect(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    if (this.ws) {
      this.ws.close(1000, "Client disconnect");
      this.ws = null;
    }

    this.status = "disconnected";
    this.activeJobId = null;
    this.reconnectAttempts = 0;
  }

  /**
   * Update callbacks
   */
  public updateCallbacks(callbacks: ChatWebSocketCallbacks): void {
    this.callbacks = { ...this.callbacks, ...callbacks };
  }
}

// Export singleton instance
const chatWebSocketService = new ChatWebSocketService();
export default chatWebSocketService;
